import React from "react";
import * as Lucide from "lucide-react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SERVICES } from "@/lib/constants";
import Seo from "@/components/Seo";
import CtaStrip from "@/components/CtaStrip";
import LenderMarquee from "@/components/LenderMarquee";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const FAQS = [
    {
        q: "How much does a mortgage broker cost in Australia?",
        a: "Finance Wealth does not charge clients a fee for residential home-loan broking — lenders pay us a commission once your loan settles. Full commission disclosures are provided in your Credit Proposal Disclosure Document."
    },
    {
        q: "Do you help borrowers Australia-wide?",
        a: "Yes. We assist clients in every state and territory. Consultations happen by phone or video call and applications are handled digitally end to end, so you get the same service wherever you live."
    },
    {
        q: "How many lenders does Finance Wealth have access to?",
        a: "We work with over 60 Australian lenders — including the big four banks, mutual banks, non-bank lenders and specialist lenders for self-employed and complex scenarios."
    },
    {
        q: "What documents do I need to apply for a home loan?",
        a: "Typically: photo ID, the last 2 payslips (or 2 years tax returns if self-employed), 3 months bank statements, current loan/credit-card statements, and the property contract once you have one. We'll guide you on exactly what your chosen lender requires."
    },
    {
        q: "Can a first home buyer access government grants?",
        a: "Yes — every state and territory offers first home owner grants and stamp duty concessions, and the national 5% Deposit Scheme can help you buy with a smaller deposit. We help you navigate the eligibility rules that apply where you are buying."
    },
    {
        q: "How long does it take to get a home loan approved?",
        a: "Pre-approval typically takes 3–10 business days depending on the lender and the complexity of your situation. Unconditional approval after you've signed a contract usually follows within 7–14 days."
    }
];

const SERVICES_FAQ_LD = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQS.map((f) => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a }
    }))
};

const detail = {
    "home-loans": "Whether you're upgrading the family home or buying your first one, we'll compare the major banks, mutual banks and non-bank lenders to find your ideal home loan — fixed, variable, split or offset.",
    "first-home-buyer": "Navigate first home owner grants, the 5% Deposit Scheme, stamp duty concessions and lender-specific programs with clear, step-by-step guidance at every stage.",
    "refinance": "A regular loan review can show whether your current loan still suits you. We will benchmark your loan, negotiate with your existing lender, or arrange a move to another lender where it makes sense.",
    "investment-loans": "Build a property portfolio with the right loan structure, interest-only options, equity releases and ownership structuring in coordination with your accountant.",
    "commercial-loans": "Commercial property purchase, owner-occupier business premises, fit-out finance and working capital — across major and specialist commercial lenders.",
    "self-employed": "Sole traders, contractors and business owners deserve smart finance. We use full-doc, alt-doc and low-doc loans to deliver competitive outcomes.",
    "car-loans": "Vehicle and equipment finance for personal cars, business utes, fleets and machinery — chattel mortgage, novated lease, hire purchase.",
    "personal-loans": "Consolidate higher-interest debt, fund a renovation or cover a life event with a competitive personal loan from a trusted lender."
};

const DETAIL_PATH = { "self-employed": "/self-employed-loans", "car-loans": "/car-asset-finance" };
const detailPath = (slug) => DETAIL_PATH[slug] || `/${slug}`;

export default function ServicesPage() {
    return (
        <div data-testid="page-services" className="pt-20">
            <Seo
                title="Mortgage Broking Services Australia | Finance Wealth"
                description="Home loans, refinance, investment and commercial loans, first home buyer, self-employed and car finance — all under one Australia-wide mortgage broker across 60+ lenders."
                keywords="mortgage broking services Australia, home loans Australia, refinance Australia, investment loans Australia, commercial loans, self-employed home loan, first home buyer Australia, car finance Australia"
                jsonLd={SERVICES_FAQ_LD}
            />
            <section className="bg-[#F3F1EA]">
                <div className="container-prose py-20 md:py-28">
                    <div className="max-w-3xl">
                        <div className="overline">Services</div>
                        <h1 className="mt-4 font-serif text-5xl sm:text-6xl tracking-tight leading-[1.02] text-[#1A362B] font-medium">
                            Finance, structured around your goals.
                        </h1>
                        <div className="divider-thin my-8" />
                        <p className="text-lg text-[#1C1C1C]/75 leading-relaxed font-light">
                            One Australia-wide online mortgage broker, one process — across home loans, refinance,
                            investment, commercial and specialist finance. Explore each service in detail below.
                        </p>
                    </div>
                </div>
            </section>

            <section className="bg-background">
                <div className="container-prose py-24 space-y-px bg-border">
                    {SERVICES.map((s, i) => {
                        const Icon = Lucide[s.icon] || Lucide.CircleDot;
                        return (
                            <div key={s.slug} data-testid={`service-row-${s.slug}`} className="bg-background grid grid-cols-1 md:grid-cols-12 gap-8 py-12 px-4 md:px-8 hover:bg-[#FAF9F6] transition-colors">
                                <div className="md:col-span-1">
                                    <div className="font-serif text-3xl text-[#1A362B]/30">0{i + 1}</div>
                                </div>
                                <div className="md:col-span-3 flex md:block items-start gap-4">
                                    <div className="w-12 h-12 rounded-sm bg-[#1A362B] text-white grid place-items-center shrink-0">
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <h3 className="md:mt-5 font-serif text-2xl text-[#1A362B] tracking-tight">{s.title}</h3>
                                </div>
                                <div className="md:col-span-8">
                                    <p className="text-[#1C1C1C]/80 leading-relaxed">{detail[s.slug]}</p>
                                    <Link to={detailPath(s.slug)} data-testid={`service-row-cta-${s.slug}`} className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-[#1A362B] border-b border-[#C69C6D] pb-1 hover:gap-3 transition-all">
                                        Explore {s.title} <ArrowRight className="w-4 h-4" />
                                    </Link>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            <section className="bg-[#F3F1EA]">
                <div className="container-prose py-24">
                    <div className="max-w-3xl">
                        <div className="overline">Common questions</div>
                        <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight leading-[1.05] text-[#1A362B] font-medium">
                            Frequently asked questions.
                        </h2>
                        <div className="divider-thin my-8" />
                        <Accordion type="single" collapsible data-testid="services-faq" className="w-full">
                            {FAQS.map((f, i) => (
                                <AccordionItem key={i} value={`faq-${i}`} data-testid={`services-faq-item-${i}`}>
                                    <AccordionTrigger className="font-serif text-lg text-[#1A362B]">{f.q}</AccordionTrigger>
                                    <AccordionContent className="text-[#1C1C1C]/75 leading-relaxed">{f.a}</AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                </div>
            </section>

            <LenderMarquee />
            <CtaStrip />
        </div>
    );
}
